import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type { PropType } from 'vue';
import { notification } from 'ant-design-vue';
import AddOfferItems from '@/components/global/popup/AddOfferItems.vue';
import DefaultValueWarnings from '@/components/global/popup/DefaultValueWarnings.vue';
import SegmentedMultiposition from '@/components/views/project/SegmentedMultiposition.vue';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { RouteNames } from '@/enums/routes/RouteNames';
import { EventBus } from '@/helpers/EventBusHelper';
import { extractInvalidDefaultValues } from '@/helpers/FieldFunctionHelper';
import {
    cssMultipositionRowElementPositioning,
    extractDefaultValues,
    extractUpdateFunctions,
    filterAndSortActiveItemRows,
} from '@/helpers/FormDataHelper';
import { checkForErrorsInFieldsAndReturnState, resetHelperState } from '@/helpers/FormValidationHelper';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { removeOfferItemFromArrayById } from '@/helpers/Products/ProductHelper';
import type { ProductForm } from '@/interfaces/components/configurator/ProductForm';
import type { ProductFormConfig } from '@/interfaces/components/configurator/ProductFormConfig';
import type { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';
import type { defaultValueWarnings as DefaultValueWarningsConfig } from '@/interfaces/components/projectNew/DefaultValueWarnings';
import type { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import type { FormValidationPayload } from '@/interfaces/general/FormValidationPayload.js';
import type { MultipositionDeleteMap } from '@/interfaces/general/MultipositionDeleteMap';
import type { ProductAndProductFormId } from '@/interfaces/general/ProductAndProductFormId';
import type { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import i18n from '@/i18n';
import OfferItem from '@/models/OfferItem';
import PopupNotifications from '@/modules/Notifications/components/PopupNotifications.vue';
import type { PopupNotificationOptions } from '@/modules/Notifications/interfaces/PopupNotificationOptions';
import OfferItemRepository from '@/repositories/OfferItemRepository';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import router from '@/router';
import ConfiguratorService from '@/services/ConfiguratorService';
import store from '@/store';

/* === Props === */

export default /*#__PURE__*/_defineComponent({
  __name: 'EditMultipositionSlot',
  props: {
    popupData: { type: Object as PropType<any>, required: true },
},
  setup(__props) {

const props = __props;



const { proxy } = getCurrentInstance()!;
const componentProxy = proxy as any;

/* === Store Access === */
const getSingleProductForm = (productFormId: string) =>
    store.dispatch('configurator/getSingleProductForm', productFormId);
const productForm = (productFormId: string): ProductForm | null =>
    store.getters['configurator/productForm'](productFormId);
const addActiveProductForm = (activeProductFormValue: ActiveProductFormValueObject): Promise<number | null> =>
    store.dispatch('configurator/addActiveProductForm', activeProductFormValue);
const resetActiveProductForms = () => store.commit('configurator/resetActiveProductForms');
const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null =>
    store.getters['configurator/activeProductForm'](productFormId);

/* === State === */
const loadingOverlay = new LoadingOverlayHelper(componentProxy, {});
const activeItemRows = ref<ProductRowItem[]>([]);
const activeProductFormConfigs = ref<ProductFormConfig[]>([]);
const offerItemIdsToBeDeleted = ref<string[]>([]);
const isAddingNewRow = ref(false);
const activeProductForm = ref<ProductForm | null>(null);
const notifications = ref<PopupNotificationOptions[][]>([]);
const flatNotifications = ref<PopupNotificationOptions[]>([]);
const formFieldErrorsExist = ref(false);
const showDefaultValueWarningsModal = ref(true);
const savedSegmentProductIds = ref<string[]>([]);
const isLockedSegmentStatuses = ref<Record<number, boolean>>({});

/* === Derived State === */
const isUpdatingData = computed(
    () => activeItemRows.value.some((activeItemRow) => activeItemRow.isUpdatingData) || isAddingNewRow.value,
);

const filteredActiveItemRows = computed(() =>
    filterAndSortActiveItemRows(
        props.popupData.productFormAndProductIds,
        activeItemRows.value,
        savedSegmentProductIds.value,
    ),
);

const defaultValueWarnings = computed(() => store.getters['configurator/defaultValueWarnings']);

const userGroup = computed(() => {
    const currentUserGroupId = store.getters['jwtData/currentUserGroup'];
    const currentUserGroup = UserGroupRepository.getById(String(currentUserGroupId));

    return currentUserGroup || currentUserGroupId;
});

/* === Product Form Setup === */
async function getCurrentProductForm(productFormId: string): Promise<ProductForm | null> {
    if (productForm(productFormId) != null) {
        return productForm(productFormId);
    }

    try {
        await getSingleProductForm(productFormId);
    } catch (error) {
        loadingOverlay.stop();
        addNotification(i18n.t('Dogodila se greška') as string, (error as Error).message, 'error');
        router.push({ name: RouteNames.error });
        return null;
    }

    return productForm(productFormId);
}

async function setDefaultProductFormSettings(productFormAndProductId: ProductAndProductFormId) {
    try {
        activeProductForm.value = await getCurrentProductForm(productFormAndProductId.productFormId);
    } catch (error) {
        return;
    }

    if (activeProductForm.value == null) {
        return Promise.reject(new Error('Greška pri dohvaćanju proizvoda'));
    }

    const defaultUpdateFunctions = extractUpdateFunctions(activeProductForm.value);
    const defaultEditableFields = ConfiguratorService.extractEditableFields(activeProductForm.value);
    const initialProductFormValues = await extractDefaultValues({
        productForm: activeProductForm.value,
        productId: productFormAndProductId.productId,
        clientId: props.popupData.clientId,
        productSystemName: props.popupData.productSystemName,
    });
    const availableSegments = ConfiguratorService.getSegmentNames(activeProductForm.value);

    activeProductFormConfigs.value.push({
        activeProductForm: activeProductForm.value,
        defaultUpdateFunctions,
        defaultEditableFields,
        productFormId: productFormAndProductId.productFormId,
        availableSegments,
        initialProductFormValues,
    });

    return Promise.resolve();
}

function productDetails(productRow: ProductRowItem) {
    const productFormId = productRow.productFormId;
    const productSystemName = productRow.offerItem?.name;
    const currentProductForm = activeProductFormConfigs.value.find(
        (rowConfig) => rowConfig.productFormId === productFormId,
    );

    return {
        productName: currentProductForm?.activeProductForm?.name ?? '',
        productCategory: currentProductForm?.activeProductForm?.directory ?? '',
        productSystemName: props.popupData?.productSystemName || productSystemName,
    };
}

/* === Rows === */
async function handleRestoreDraft(restoredRows: any[]) {
    for (const rowData of restoredRows) {
        notifications.value.push([]);

        const rowFormId = String(rowData.activeProductFormId);
        const config = activeProductFormConfigs.value.find((item) => String(item.productFormId) === rowFormId);

        if (!config) {
            continue;
        }

        try {
            const mockOfferItem = {
                ...rowData,
                offerItemProductSystem: {
                    product: {
                        id: config.activeProductForm?.productId,
                        name: config.activeProductForm?.name,
                        productForm: { id: rowFormId },
                    },
                },
            };

            const newRows = await ConfiguratorService.addNewRow({
                initialProductFormValue: config.initialProductFormValues,
                updateFunctions: config.defaultUpdateFunctions,
                editableFields: config.defaultEditableFields,
                availableSegments: config.availableSegments,
                addActiveProductForm,
                offerItem: mockOfferItem as any,
                productFormId: rowFormId,
                existingProductValues: rowData.formData,
                clientId: props.popupData.clientId,
                isCustom: activeProductForm.value ? activeProductForm.value.isCustom : false,
            });

            activeItemRows.value.push(...newRows);
        } catch (error) {
            console.error('[Restore] Failed for row:', error);
        }
    }
}

async function addExistingOfferItemRow(offerItemId: string) {
    isAddingNewRow.value = true;
    notifications.value.push([]);

    let newItemRows: ProductRowItem[] = [];
    const offerItem = await OfferItemRepository.getById(offerItemId);

    if (offerItem == null) {
        return Promise.reject(new Error('Offer item not found'));
    }

    const currentActiveProductFormConfig = activeProductFormConfigs.value.find(
        (activeProductFormConfig: ProductFormConfig) => {
            const activeProductFormId = activeProductFormConfig.productFormId;
            const offerItemProductFormId = offerItem.offerItemProductSystem.product.productForm.id;

            return activeProductFormId === offerItemProductFormId;
        },
    );

    if (currentActiveProductFormConfig === undefined) {
        return Promise.reject(new Error('Active product form not found'));
    }

    try {
        newItemRows = await ConfiguratorService.addNewRow({
            initialProductFormValue: currentActiveProductFormConfig.initialProductFormValues,
            updateFunctions: currentActiveProductFormConfig.defaultUpdateFunctions,
            editableFields: currentActiveProductFormConfig.defaultEditableFields,
            availableSegments: currentActiveProductFormConfig.availableSegments,
            addActiveProductForm,
            offerItem,
            existingProductValues: offerItem.offerItemFormData,
            clientId: props.popupData.clientId,
            isCustom: activeProductForm.value ? activeProductForm.value.isCustom : false,
        });
    } catch (error) {
        addNotification(i18n.t('Dogodila se greška') as string, (error as Error).message, 'error');
        isAddingNewRow.value = false;
        return Promise.reject(error);
    }

    activeItemRows.value.push(...newItemRows);
    isAddingNewRow.value = false;

    return Promise.resolve();
}

async function addNewRow(numberOfRows: number, existingProductValues?: ChecklistFieldEntry[]) {
    const firstActiveItemRow = activeItemRows.value[0];

    if (!firstActiveItemRow) {
        return;
    }

    for (let index = 0; index < numberOfRows; index++) {
        await createNewItemRow(firstActiveItemRow, existingProductValues);
    }
}

async function createNewItemRow(
    productRowItem: ProductRowItem,
    existingProductValues?: ChecklistFieldEntry[],
    isNewRowInEditMode: boolean = false,
) {
    let newItemRows: ProductRowItem[] = [];
    const productFormId = productRowItem.productFormId;
    const currentProductForm = productForm(productFormId as string);
    const productSystemName = productRowItem.offerItem?.productSystem?.code;
    notifications.value.push([]);

    loadingOverlay.start();

    const initialProductFormValues = await extractDefaultValues({
        productForm: currentProductForm as ProductForm,
        productId: productRowItem.productId as string,
        clientId: props.popupData.clientId,
        productSystemName: props.popupData.productSystemName ?? productSystemName,
    });

    const defaultUpdateFunctions = extractUpdateFunctions(currentProductForm as ProductForm);
    const defaultEditableFields = ConfiguratorService.extractEditableFields(currentProductForm as ProductForm);
    const availableSegments = ConfiguratorService.getSegmentNames(currentProductForm);

    try {
        newItemRows = await ConfiguratorService.addNewRow({
            initialProductFormValue: initialProductFormValues,
            updateFunctions: defaultUpdateFunctions,
            editableFields: defaultEditableFields,
            availableSegments,
            addActiveProductForm,
            offerItem: productRowItem.offerItem as OfferItem,
            shouldSetOfferItem: false,
            existingProductValues,
            clientId: props.popupData.clientId,
            isCustom: activeProductForm.value ? activeProductForm.value.isCustom : false,
            isNewRowInEditMode,
        });
    } catch (error) {
        addNotification(i18n.t('Dogodila se greška') as string, (error as Error).message, 'error');
        return Promise.reject(error);
    }

    activeItemRows.value.push(...newItemRows);
    loadingOverlay.stop();

    verifyAndFixNewRowPositioning(newItemRows[0].activeProductFormId);

    return Promise.resolve();
}

function updateItemRowsIsUpdatingState(activeProductFormId: number, newState: boolean) {
    const itemRowIndex = findRowIndexByProductFormIdInActiveItemRows(activeProductFormId);
    activeItemRows.value[itemRowIndex].isUpdatingData = newState;
}

async function verifyAndFixNewRowPositioning(activeProductFormId: number) {
    await nextTick();
    const rowElement = document.getElementById(String(activeProductFormId));

    cssMultipositionRowElementPositioning(rowElement);
}

function deleteExistingRow({ offerItemId, activeProductFormId }: MultipositionDeleteMap) {
    if (activeItemRows.value.length === 1) {
        addNotification(
            i18n.t('Dogodila se greška') as string,
            i18n.t('Nije moguće obrisati zadnji redak.') as string,
            'error',
        );
        return;
    }

    markOfferItemToBeDeleted({ offerItemId, activeProductFormId });
}

function markOfferItemToBeDeleted({ offerItemId, activeProductFormId }: MultipositionDeleteMap) {
    const rowIndexToBeDeleted = findRowIndexByProductFormIdInActiveItemRows(activeProductFormId);

    if (rowIndexToBeDeleted === -1) {
        return;
    }

    activeItemRows.value.splice(rowIndexToBeDeleted, 1);

    if (offerItemId != null) {
        offerItemIdsToBeDeleted.value.push(offerItemId);
    }

    updateOfferItemIdsForUpdatingRowNumbers(offerItemId);
    notifications.value.splice(rowIndexToBeDeleted, 1);
    flatNotifications.value = notifications.value.flat();
}

function updateOfferItemIdsForUpdatingRowNumbers(offerItemId: string | null) {
    if (offerItemId == null) {
        return;
    }

    props.popupData.offerItemIdsForUpdatingRowNumbers = removeOfferItemFromArrayById(
        offerItemId,
        props.popupData.offerItemIdsForUpdatingRowNumbers,
    );
}

function findRowIndexByProductFormIdInActiveItemRows(activeProductFormId: number) {
    return activeItemRows.value.findIndex(
        (itemRow: ProductRowItem) => itemRow.activeProductFormId === activeProductFormId,
    );
}

/* === Child Component State === */
function updateLockedSegmentStatus(isLocked: boolean, index: number) {
    componentProxy.$set(isLockedSegmentStatuses.value, index, isLocked);
}

function productSlotRowsReferences() {
    const segmentedMultipositionReferences = componentProxy.$refs.segmentedMultiposition;
    const segmentedMultipositions = Array.isArray(segmentedMultipositionReferences)
        ? segmentedMultipositionReferences
        : segmentedMultipositionReferences
        ? [segmentedMultipositionReferences]
        : [];

    return segmentedMultipositions
        .map((segmentedMultiposition: any) => segmentedMultiposition.$refs?.productSlotRow ?? [])
        .flat();
}

/* === Notifications & Validation === */
function addNotification(title: string, description: string, errorType: 'error' | 'warning', index = 0) {
    notifications.value[index].push({
        title: i18n.t(title) as string,
        description: i18n.t(description) as string,
        errorType,
        index: index + 1,
    });
    flatNotifications.value = notifications.value.flat();
}

function onShowNotification(event: any) {
    addNotification(event.title, event.description, event.type, event.index);
}

function onClearNotifications(index: number) {
    notifications.value[index] = [];
    flatNotifications.value = notifications.value.flat();
}

async function validateFormFields(activeRows: ProductRowItem[] = activeItemRows.value) {
    formFieldErrorsExist.value = false;
    const formValues: FormValidationPayload[] = [];

    for (const itemRow of activeRows) {
        const rowValues = getProductFormValues(itemRow.activeProductFormId);

        formValues.push({
            rowKey: itemRow.activeProductFormId,
            values: { ...rowValues },
            fieldSegments: itemRow.editableFields as any,
        });
    }

    const errorState = checkForErrorsInFieldsAndReturnState(formValues);
    formFieldErrorsExist.value = errorState.hasErrors;

    if (errorState.hasErrors) {
        showErrorState(errorState.errorDescription);
    }

    EventBus.$emit(EventBusEvents.formValidationFinished);
}

function showErrorState(description: string) {
    notification.error({
        message: i18n.t('Invalid characters detected in form fields:') as string,
        description,
    });
}

async function determineInvalidDefaultValues() {
    const invalidDefaultValues: DefaultValueWarningsConfig[] = [];

    activeItemRows.value.forEach((itemRow: any) => {
        const activeRowFields = itemRow.editableFields;
        const currentProductFormValues = getProductFormValues(itemRow.activeProductFormId);
        const warnings: any[] = extractInvalidDefaultValues(
            activeRowFields,
            currentProductFormValues,
            itemRow.activeProductFormId,
        );

        invalidDefaultValues.push({
            id: itemRow.offerItem?.displayRowNumber ?? itemRow.activeProductFormId,
            productName: itemRow.productName,
            productId: itemRow.productId,
            productFormId: itemRow.activeProductFormId,
            warnings,
        });
    });

    await store.dispatch('configurator/setDefaultValueWarnings', invalidDefaultValues);

    return Promise.resolve();
}

function onCancel() {
    EventBus.$emit(EventBusEvents.closePopup, true);
}

function closeModal() {
    showDefaultValueWarningsModal.value = false;
}

function removeSegmentFromMultiposition(productFormId: string) {
    savedSegmentProductIds.value.push(productFormId);
}

function closeMultiposition() {
    if (filteredActiveItemRows.value.length) {
        return;
    }

    EventBus.$emit(EventBusEvents.closePopup);
}

watch(filteredActiveItemRows, closeMultiposition);

/* === Initialization & Cleanup === */
(async () => {
    resetHelperState();
    loadingOverlay.start();

    for await (const productFormAndProductId of props.popupData.productFormAndProductIds) {
        try {
            await setDefaultProductFormSettings(productFormAndProductId);
        } catch (error) {
            addNotification(i18n.t('Dogodila se greška') as string, (error as Error).message, 'error');
            loadingOverlay.stop();
            return;
        }
    }

    if (props.popupData.isRestoringDraft && props.popupData.restoredRowData) {
        await handleRestoreDraft(props.popupData.restoredRowData);
        notification.success({
            message: i18n.t('Draft restored') as string,
            description: i18n.t('Successfully restored draft items.') as string,
        });
    } else {
        await Promise.all(
            props.popupData.offerItemIds.map((offerItemId: string) => addExistingOfferItemRow(offerItemId)),
        );
    }

    await nextTick();
    determineInvalidDefaultValues();
    loadingOverlay.stop();

    EventBus.$on(EventBusEvents.showNotification, onShowNotification);
    EventBus.$on(EventBusEvents.clearNotifications, onClearNotifications);
    EventBus.$on(EventBusEvents.removeSavedMultipositionSegment, removeSegmentFromMultiposition);
})();

onBeforeUnmount(() => {
    notifications.value = [];
    flatNotifications.value = [];
    resetActiveProductForms();
    store.dispatch('configurator/setDefaultValueWarnings', []);
    setTimeout(() => {
        notification.destroy();
    }, 2000);
    EventBus.$off(EventBusEvents.showNotification, onShowNotification);
    EventBus.$off(EventBusEvents.clearNotifications, onClearNotifications);
    EventBus.$off(EventBusEvents.removeSavedMultipositionSegment, removeSegmentFromMultiposition);
});

return { __sfc: true,props, proxy, componentProxy, getSingleProductForm, productForm, addActiveProductForm, resetActiveProductForms, getProductFormValues, loadingOverlay, activeItemRows, activeProductFormConfigs, offerItemIdsToBeDeleted, isAddingNewRow, activeProductForm, notifications, flatNotifications, formFieldErrorsExist, showDefaultValueWarningsModal, savedSegmentProductIds, isLockedSegmentStatuses, isUpdatingData, filteredActiveItemRows, defaultValueWarnings, userGroup, getCurrentProductForm, setDefaultProductFormSettings, productDetails, handleRestoreDraft, addExistingOfferItemRow, addNewRow, createNewItemRow, updateItemRowsIsUpdatingState, verifyAndFixNewRowPositioning, deleteExistingRow, markOfferItemToBeDeleted, updateOfferItemIdsForUpdatingRowNumbers, findRowIndexByProductFormIdInActiveItemRows, updateLockedSegmentStatus, productSlotRowsReferences, addNotification, onShowNotification, onClearNotifications, validateFormFields, showErrorState, determineInvalidDefaultValues, onCancel, closeModal, removeSegmentFromMultiposition, closeMultiposition, AddOfferItems, DefaultValueWarnings, SegmentedMultiposition, PopupNotifications }
}

})